import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button, Alert } from "@/shared/ui";
import { XMarkIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { supabase } from "@/shared/db";
import { toast } from "sonner";
import { analytics } from "../utils/analytics";

export const EmailVerificationBanner: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem("emailVerificationDismissed") === "true";
  });

  // Check if email is verified
  const isEmailVerified = user?.email_confirmed_at !== null;

  if (isEmailVerified || isDismissed || !user) {
    return null;
  }

  const handleResendVerification = async () => {
    if (!user?.email) return;

    try {
      setIsResending(true);
      analytics.track("email_verification_resent", {
        user_id: user.id,
      });

      const { error } = await supabase.auth.resend({
        type: "signup",
        email: user.email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`,
        },
      });

      if (error) throw error;

      toast.success(t("auth.emailVerification.resent"));
    } catch (err) {
      const msg = (err as { message?: string })?.message;
      console.error("Error resending verification email:", err);
      toast.error(msg || t("auth.emailVerification.error"), { duration: 6000 });
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem("emailVerificationDismissed", "true");
    analytics.track("email_verification_dismissed", {
      user_id: user?.id,
    });
  };

  return (
    <div className="mb-4">
      <Alert
        variant="warning"
        title={t("auth.emailVerification.title")}
        description={t("auth.emailVerification.description")}
      />
      <div className="flex gap-2 mt-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleResendVerification}
          disabled={isResending}
          leftIcon={<ArrowPathIcon className="h-4 w-4" />}
        >
          {isResending
            ? t("auth.emailVerification.sending")
            : t("auth.emailVerification.resend")}
        </Button>
        <button
          onClick={handleDismiss}
          className="p-1 text-tiber-600 hover:text-tiber-900"
          aria-label={t("common.close")}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};
